import { useCallback, useEffect, useRef } from 'react';
import { App } from '@capacitor/app';
import { Device } from '@capacitor/device';
import { Geolocation } from '@capacitor/geolocation';
import { useDocumentVisibility, useLatest, useLocalStorageState, useMemoizedFn } from 'ahooks';
import { capLocation } from 'capacitor-plugin-location-coord';
import semver from 'semver';
import lookie from 'lookie';
import { Dialog } from 'antd-mobile';
import { useSelector } from 'react-redux';
import { TrackerCustomEvent } from 'common-package';
import { NativeSettings, AndroidSettings } from 'capacitor-native-settings';
import { APP_LOCATION_IS_UNAVAILABLE, COORDINATE_TYPE, CURRENT_ANDROID_ID, STORAGE_LOCATION_COORDS } from '@/constant';
import { selectedApkUpdateStatus } from '@/store/device';
import service from '@/service';
import { useDevice } from './useDevice';
import { useLogin, useTracker } from './index';

/** 控制获取位置信息的最小间隔 */
const GET_LOC_INTERVAL = 12 * 60 * 60 * 1000;
/** 调用getCurrentPosition方法的超时时间 */
const GET_LOC_TIMEOUT = 15 * 1000;
/** 获取位置时允许的最大内存占用（bytes） */
const GET_LOC_ALLOW_MAX_MEMORY = 100 * 1024 * 1024;

/**
 * 获取设备定位信息
 */
const useGetLocation = () => {
  const { info } = useDevice();
  const visiblility = useDocumentVisibility();
  const { isLogin } = useLogin();
  const { sendEvent } = useTracker();
  const latestSendEvent = useLatest(sendEvent);
  const hasShowDialog = useRef(false);
  const apkUpdateStatus = useSelector(selectedApkUpdateStatus);
  const [lastGetLocationTime, setLastGetLocationTime] = useLocalStorageState<number>('last-get-location-time');
  const lastGetLocationTimeRef = useLatest(lastGetLocationTime);
  const isGetLocation = useRef(false);
  const getLocationInterval = useRef(null);

  /** 第一次点拒绝后，系统会自动弹起第二个弹窗，获取该弹窗的回调 */
  const intervalGetLocationPermissionOnDeny = useMemoizedFn(async () => {
    getLocationInterval.current = setInterval(async () => {
      console.log('正在获取授权状态');
      const geoPermission = await Geolocation.checkPermissions();
      if (geoPermission?.coarseLocation === 'granted' && geoPermission?.location === 'granted') {
        Dialog.clear();
        hasShowDialog.current = false;
        clearInterval(getLocationInterval.current);

        preparePermission(handleOnGetLocationPermission);
      }
    }, 2000);

    /** 系统弹窗只会几秒，轮询到20s后就停止 */
    setTimeout(() => clearInterval(getLocationInterval.current), 20 * 1000);
  });

  const handleOnDenyPermission = useCallback(
    (props?: { isNotOpenGPS?: boolean }) => {
      const { isNotOpenGPS } = props || {};

      if (hasShowDialog.current) return;

      hasShowDialog.current = true;

      /** 第一次点拒绝后，系统会自动弹起第二个弹窗，获取该弹窗的回调 */
      intervalGetLocationPermissionOnDeny();
      Dialog.alert({
        title: isNotOpenGPS ? '开启平板定位服务' : '开启定位权限',
        content: isNotOpenGPS ? (
          <div>
            <span>
              检测到平板定位服务被关闭，请前往
              <br />
              <span style={{ color: '#004ff9', fontWeight: 700 }}>{`设置->通用设置->安全和隐私`}</span>
              <br />
              开启平板的位置信息的权限
            </span>
          </div>
        ) : (
          <div>
            <span>
              系统检测到定位权限被关闭
              <br />
              或者未开启确切位置，请前往
              <br />
              <span style={{ color: '#004ff9', fontWeight: 700 }}>{`理疗师工作台->权限->定位`}</span>
              <br />
              开启理疗师工作台的定位权限
            </span>
          </div>
        ),
        confirmText: '去开启',
        onConfirm() {
          NativeSettings.openAndroid({
            option: isNotOpenGPS ? AndroidSettings.Location : AndroidSettings.ApplicationDetails,
          })
            .then(status => {
              console.log('打开系统设置状态：', status);
            })
            .catch(e => {
              console.error('NativeSettings.openAndroid Error：', e);
            });
          return Promise.reject('');
        },
      });
    },
    [intervalGetLocationPermissionOnDeny]
  );

  const preparePermission = useCallback(
    async (handleOnGetPermission: VoidFunc) => {
      if (!info) return;

      if (lookie.get(APP_LOCATION_IS_UNAVAILABLE)) {
        /** app当前获取不到权限，忽略获取位置信息 */
        console.log('app当前获取不到权限，忽略获取位置信息');
        return;
      }

      if (info.platform === 'android') {
        // 获取到位置后，控制至少间隔设定时间获取一次位置
        if (
          lastGetLocationTimeRef.current &&
          new Date().getTime() - lastGetLocationTimeRef.current < GET_LOC_INTERVAL
        ) {
          return;
        }

        try {
          const geoPermission = await Geolocation.checkPermissions();
          console.log('当前的permission:', geoPermission);
          if (geoPermission.location === 'denied' && geoPermission.coarseLocation === 'denied') {
            // 所有权限关闭，指引跳转
            console.log('所有权限关闭，指引跳转 from init');
            handleOnDenyPermission();
          } else if (geoPermission.location === 'granted' && geoPermission.coarseLocation === 'granted') {
            handleOnGetPermission?.();
          } else {
            loopRequestPermission?.(handleOnGetPermission, () => {
              // 所有权限关闭，指引跳转
              console.log('所有权限关闭，指引跳转 from cb');
              handleOnDenyPermission();
            });
          }
        } catch (e) {
          console.error('location error:', e);
          handleOnDenyPermission({ isNotOpenGPS: true });
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [info]
  );

  const loopRequestPermission = useCallback(async (onOK: VoidFunc, onDeny: VoidFunc) => {
    console.log('开始请求位置权限。。。');
    const result = await Geolocation.requestPermissions({
      permissions: ['location', 'coarseLocation'],
    });
    console.log('请求权限的结果', result);

    if (result.location === 'prompt' && result.coarseLocation === 'prompt') {
      setTimeout(() => loopRequestPermission(onOK, onDeny), 500);
    } else if (result.location === 'granted' && result.coarseLocation === 'granted') {
      onOK?.();
    } else {
      onDeny?.();
    }
  }, []);

  // 拿到位置权限的回调
  const handleOnGetLocationPermission = useCallback(() => {
    Dialog.clear();
    hasShowDialog.current = false;
    clearInterval(getLocationInterval.current);
    let timer = null;
    console.log('检测到已进行位置授权，开始获取位置信息。。。');
    setLastGetLocationTime(new Date().getTime());

    App.getInfo().then(appInfo => {
      // app在 v1.11.0 版本后替换为百度SDK
      if (semver.gte(appInfo.version, '1.11.0')) {
        // app在 v1.13.0 版本后新增wifi和基站定位
        if (semver.gte(appInfo.version, '1.13.0')) {
          capLocation
            .getCoords()
            .then(wifiLocInfo => {
              const formatWifiLocInfo: typeof wifiLocInfo = {
                ...(wifiLocInfo || ({} as any)),
                lat: +wifiLocInfo?.lat?.toFixed?.(6),
                lng: +wifiLocInfo?.lng?.toFixed?.(6),
                addressDesc: {
                  ...(wifiLocInfo?.addressDesc || ({} as any)),
                  addressLat: +wifiLocInfo?.addressDesc?.addressLat?.toFixed?.(6),
                  addressLng: +wifiLocInfo?.addressDesc?.addressLng?.toFixed?.(6),
                },
              };

              console.log('WIFI和基站获取的定位信息', formatWifiLocInfo);

              lookie.set(STORAGE_LOCATION_COORDS, {
                lat: formatWifiLocInfo.lat,
                lng: formatWifiLocInfo.lng,
                timestamp: Date.now(),
                coordType: COORDINATE_TYPE.WGS84,
              });

              service.system.recordLocation({
                locationData: null,
                errorMessage: null,
                locationCustomData: JSON.stringify(formatWifiLocInfo),
              });
            })
            .catch(e => {
              console.log('获取wifi和基站定位失败', e?.toString?.());

              console.time('【百度】');

              window.baidumap_location.getCurrentPosition(
                result => {
                  console.log('baidu location', result, JSON.stringify(result, null, 4));
                  console.timeEnd('【百度】');

                  /** 记录百度获取定位成功而WIFI和基站获取失败的情况 */
                  latestSendEvent.current?.(TrackerCustomEvent.GetWifiLocError, {
                    et: 'monitor',
                    c1: e?.toString?.(),
                  });

                  lookie.set(STORAGE_LOCATION_COORDS, {
                    lat: result.latitude,
                    lng: result.longitude,
                    timestamp: Date.now(),
                    coordType: COORDINATE_TYPE.BAIDU,
                  });
                  service.system.recordLocation({
                    locationData: JSON.stringify(result),
                    errorMessage: null,
                    locationCustomData: null,
                  });
                },
                error => {
                  console.log('【baidumap_location】getCurrentPosition error::', error);
                  service.system.recordLocation({
                    locationData: null,
                    errorMessage: JSON.stringify(error),
                  });
                  latestSendEvent.current?.(TrackerCustomEvent.DeviceGPSAbnormal, {
                    et: 'monitor',
                    c1: lookie.get(CURRENT_ANDROID_ID),
                    c2: JSON.stringify(error),
                  });
                }
              );
            });
        } else {
          console.log('旧版本APP不兼容，已设置强制更新');
        }
      } else {
        console.log('【gaode_location】开始获取定位');
        console.time('【高德】');
        window.AmapLocation?.getCurrentPosition(
          info => {
            const infoString = JSON.stringify(info);
            console.log('获取的位置信息:', infoString);

            console.timeEnd('【高德】');
            isGetLocation.current = true;
            clearTimeout(timer);

            if (info.status === 'success') {
              service.system.recordLocation({
                locationData: infoString,
                errorMessage: null,
              });
            } else {
              service.system.recordLocation({
                locationData: null,
                errorMessage: infoString,
              });
            }
          },
          error => {
            console.error('AmapLocation.getCurrentPosition Error', error);
            service.system.recordLocation({
              locationData: null,
              errorMessage: error?.message || error?.toString(),
            });
            latestSendEvent.current?.(TrackerCustomEvent.DeviceGPSAbnormal, {
              et: 'monitor',
              c1: lookie.get(CURRENT_ANDROID_ID),
              c2: JSON.stringify(error),
            });
          }
        );

        timer = setTimeout(() => {
          if (!isGetLocation.current) {
            Device.getInfo().then(info => {
              /** 获取位置信息超时且内存异常，设置flag */
              if (info.memUsed > GET_LOC_ALLOW_MAX_MEMORY) {
                lookie.set(APP_LOCATION_IS_UNAVAILABLE, true);
                latestSendEvent.current?.(TrackerCustomEvent.DeviceGPSAbnormal, {
                  et: 'monitor',
                  c1: lookie.get(CURRENT_ANDROID_ID),
                  c2: JSON.stringify(info),
                });
              }
            });
          }
        }, GET_LOC_TIMEOUT);
      }
    });
  }, [latestSendEvent, setLastGetLocationTime]);

  useEffect(() => {
    if (isLogin && visiblility === 'visible' && apkUpdateStatus === 'not-update') {
      setTimeout(() => preparePermission(handleOnGetLocationPermission), 1200);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLogin, preparePermission, visiblility, apkUpdateStatus]);
};

export default useGetLocation;
